// Simple centered modal dialog.
//
// The footer is rendered HERE, not by each screen, so the button order is
// structural rather than a convention every new modal has to remember: save is
// always first in DOM order and cancel second, which the RTL rule in ui.css
// turns into save leftmost with cancel immediately to its right.
//
// Pass `onSave` to get the footer; a modal that is read-only just omits it.
//
// The dialog also gates its own Save on the fields inside it: a control that cannot
// yet produce a value (a date box mid-type) registers itself through
// FieldValidityContext, and Save stays disabled while any has. The message NAMES the
// waiting fields — with two dates on one form, "something is unfinished" would just
// start a hunt. This is why the gating lives here and not in each screen: every field
// is inside a Modal already, so nothing at the call sites has to know about it.
import { useMemo } from 'react';
import he from '../i18n/he';
import Button from './Button';
import { FieldValidityContext, useFieldRegistry } from './FormValidity';

export default function Modal({ title, onClose, onSave, saveLabel, saveDisabled, children }) {
  const { unfinished, report } = useFieldRegistry();
  // Memoized so the context value is referentially stable — fields register from an
  // effect keyed on it, and a fresh object each render would re-register forever.
  const validity = useMemo(() => ({ report }), [report]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>{title}</h2>
          <Button variant="subtle" small onClick={onClose}>
            {he.common.close}
          </Button>
        </div>
        <FieldValidityContext.Provider value={validity}>
          {children}
        </FieldValidityContext.Provider>
        {onSave && (
          <>
            {unfinished.length > 0 && (
              <p className="field-error">{he.common.unfinishedFields(unfinished)}</p>
            )}
            <div className="actions">
              <Button onClick={onSave} disabled={saveDisabled || unfinished.length > 0}>
                {saveLabel || he.common.save}
              </Button>
              <Button variant="subtle" onClick={onClose}>
                {he.common.cancel}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
